/* eslint-disable css-modules/no-unused-class, no-return-assign */
import { FC, Fragment, useCallback, useRef, useState, useEffect } from 'react';
import classNames from 'classnames';
import { Box } from '@mui/material';
import { Icon } from 'common-components';
import {
    IconButton,
    KeyboardArrowDownIcon,
    KeyboardArrowUpIcon,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Collapse
} from '../uiLibraryImports';
import { AccordionTableProps } from './accordionTableTypes';
import styles from './accordionTable.module.scss';
import PieChart from '../progressPie/pieChart';

const AccordionTable: FC<AccordionTableProps> = (props: AccordionTableProps) => {
    const {
        rows,
        title,
        titleCount,
        titleChartPercentage,
        isOpen,
        hasMoreRows,
        loadMoreRows,
        tableHeight = '40rem',
        showTitle = true,
        isEmpty,
        emptyText,
        emptyIconName,
        headers,
        cellsWidths = [],
        firstStickyRow,
        noTopBorderRadius,
        noBottomBorderRadius,
        setIsOpen,
        newTableLayout,
        titleIconName
    } = props;
    const [open, setOpen] = useState(isOpen);
    const observer = useRef<IntersectionObserver | null>(null);
    const lastRowElementRef = useCallback(
        node => {
            if (observer.current) observer.current.disconnect();
            observer.current = new IntersectionObserver(entries => {
                if (entries[0].isIntersecting) {
                    hasMoreRows && loadMoreRows?.();
                }
            });
            if (node) observer.current.observe(node);
        },
        [loadMoreRows, loadMoreRows]
    );

    useEffect(() => {
        setIsOpen && setIsOpen(open);
    }, [open]);

    const getHeaderClass = () => {
        if (newTableLayout) {
            if (open) {
                return styles['main-collapse-line-open-new'];
            }
            return styles['main-collapse-line-new'];
        }
        if (open) {
            return styles['main-collapse-line-open'];
        }
        return styles['main-collapse-line'];
    };

    const headerClassName = getHeaderClass();

    return (
        <div
            className={classNames(styles['simple-table-root-dark'], {
                [styles['table-with-no-bottom-radius']]: noBottomBorderRadius,
                [styles['table-with-no-top-radius']]: noTopBorderRadius
            })}
            style={
                {
                    '--tableHeight': tableHeight
                } as any
            }
        >
            <TableContainer component={Paper}>
                <Table>
                    <TableBody>
                        <Fragment>
                            {headers && (
                                <TableRow className={classNames(styles.header)}>
                                    {headers?.map((rowItem, rowIndex) => (
                                        <TableCell
                                            key={rowIndex}
                                            className={classNames({
                                                [styles['cell-width']]: cellsWidths?.length > 0
                                            })}
                                            style={
                                                {
                                                    '--cellWidth': cellsWidths[rowIndex]
                                                } as any
                                            }
                                        >
                                            {rowItem}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            )}
                            {firstStickyRow && (
                                <TableRow className={classNames(styles.row, styles['sticky-row'])}>
                                    {firstStickyRow?.map((rowItem, rowIndex) => (
                                        <TableCell
                                            key={rowIndex}
                                            className={classNames({
                                                [styles['cell-width']]: cellsWidths?.length > 0
                                            })}
                                            style={
                                                {
                                                    '--cellWidth': cellsWidths[rowIndex]
                                                } as any
                                            }
                                        >
                                            {rowItem}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            )}
                            {showTitle && (
                                <TableRow className={headerClassName} data-testid="main-collapse-line">
                                    <TableCell colSpan={titleChartPercentage !== 0 && titleChartPercentage ? 3 : 4}>
                                        <div className={styles.title}>
                                            <IconButton
                                                size="small"
                                                onClick={() => setOpen(!open)}
                                                data-testid="toggle-icon"
                                            >
                                                {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                            </IconButton>

                                            <Icon name={titleIconName} className={styles.icon} />
                                            {!titleCount ? title : `${title} (${titleCount})`}
                                        </div>
                                    </TableCell>
                                    {titleChartPercentage !== 0 && titleChartPercentage && (
                                        <TableCell align="left">
                                            {
                                                <div className={newTableLayout ? styles['new-pie'] : styles.pie}>
                                                    <div className={styles['pie-text']}>{`${Math.trunc(
                                                        titleChartPercentage
                                                    )}%`}</div>
                                                    {newTableLayout ? (
                                                        <div
                                                            style={{
                                                                position: 'absolute',
                                                                top: '-0.8rem',
                                                                right: '1rem'
                                                            }}
                                                        >
                                                            <PieChart
                                                                percentage={titleChartPercentage}
                                                                showPercentage={false}
                                                                size={25}
                                                                lineWidth={3}
                                                                top="0"
                                                                left="-0.8rem"
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div
                                                            style={
                                                                {
                                                                    '--chartPercentage': `${
                                                                        titleChartPercentage * 3.6
                                                                    }deg`
                                                                } as any
                                                            }
                                                            className={styles['pie-chart']}
                                                        ></div>
                                                    )}
                                                </div>
                                            }
                                        </TableCell>
                                    )}
                                </TableRow>
                            )}
                            <TableRow>
                                <TableCell
                                    style={{ padding: '0px !important' }}
                                    colSpan={6}
                                    className={styles['collapse-table']}
                                >
                                    <Collapse in={open} timeout="auto" unmountOnExit>
                                        <Box
                                            className={styles['main-box']}
                                            style={
                                                {
                                                    '--tableHeight': tableHeight
                                                } as any
                                            }
                                        >
                                            <Table data-testid="main-collapse-line-open">
                                                <TableBody>
                                                    {!isEmpty ? (
                                                        rows.map((row, i) => (
                                                            <Fragment key={i}>
                                                                <TableRow
                                                                    key={i}
                                                                    ref={
                                                                        rows.length === i + 1
                                                                            ? lastRowElementRef
                                                                            : undefined
                                                                    }
                                                                    onClick={() =>
                                                                        row.isCollapseAble && row.onChangeOpenMode
                                                                            ? row?.onChangeOpenMode(!row.isOpen)
                                                                            : undefined
                                                                    }
                                                                    className={classNames(styles.row, {
                                                                        [styles['collapse-line']]: row.isClickable,
                                                                        [styles['last-row']]:
                                                                            i === rows.length - 1 && !row.isOpen,
                                                                        [styles['row-color']]: i === 0 && newTableLayout
                                                                    })}
                                                                >
                                                                    {row.row.map((rowItem, rowIndex) => (
                                                                        <TableCell
                                                                            className={classNames({
                                                                                [styles['cell-width']]:
                                                                                    cellsWidths?.length > 0
                                                                            })}
                                                                            key={rowIndex}
                                                                            style={
                                                                                {
                                                                                    '--cellWidth': cellsWidths[rowIndex]
                                                                                } as any
                                                                            }
                                                                        >
                                                                            {rowItem}
                                                                        </TableCell>
                                                                    ))}
                                                                </TableRow>
                                                                {row.isCollapseAble && row?.innerTable}
                                                            </Fragment>
                                                        ))
                                                    ) : (
                                                        <div className={styles['table-empty-state']}>
                                                            <Icon name={emptyIconName} />
                                                            <div className={styles['empty-state-text']}>
                                                                {emptyText}
                                                            </div>
                                                        </div>
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </Box>
                                    </Collapse>
                                </TableCell>
                            </TableRow>
                        </Fragment>
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
};

export default AccordionTable;
